import {
  Box,
  Collapse,
  Flex,
  FlexProps,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Packaging, PricePointAttributes, ProductCalculation } from 'api-client'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { debounce } from 'throttle-debounce'

import { IconCircleAlert } from '~shared-components/icons/brand/IconCircleAlert'
import { IconLocked } from '~shared-components/icons/brand/IconLocked'

import { fetchPricesAndOrderAmounts } from '../../api/cart'
import { useToaster } from '../../hooks/useToaster'
import { useTranslations } from '../../hooks/useTranslations'
import { useUom } from '../../hooks/useUom'
import { BaseCartProduct } from '../../model/cart.model'
import { ConfigSelector } from '../../redux/selectors/config.selector'
import { Price as PriceType } from '../../transforms/price.transforms'
import { getMinimumOrderAmount } from '../../utils/price-utils'
import { InfoMessage } from '../InfoMessage/InfoMessage'
import { Input } from '../Input/Input'
import { InputLabel } from '../Input/InputLabel'
import { LoadingBars } from '../Loader/LoadingBars'
import { NoPriceAvailable } from '../Price/NoPriceAvailable'
import { Price } from '../Price/Price'
import { TierPrices } from '../Price/TierPrices'
import { CaptionText } from '../Typography/Typography'
import { GenericModalFooter } from './GenericModalFooter'
import { ModalCloseButton } from './ModalCloseButton'

const PriceFooter: React.FC<PriceFooterProps> = ({ show, ...props }) => (
  <Flex
    alignItems="center"
    backgroundColor="neutral.10"
    flexWrap="nowrap"
    h="40px"
    justifyContent={{ base: 'space-between', md: 'flex-end' }}
    opacity={show ? 1 : 0}
    p={1}
    width="100%"
    {...props}
  />
)

interface PriceFooterProps extends FlexProps {
  show: boolean
}

const fetchPriceAndOrderAmount = async (
  apiBaseUrl: string,
  shipToLocationId: string | undefined,
  product: BaseCartProduct | undefined,
  quantity: number,
) => {
  const fetchPriceAndOrderAmountFromApi = async () => {
    if (shipToLocationId && product && quantity && quantity > 0) {
      const {
        data: { id: productId },
      } = product

      const pricesAndOrderAmounts = await fetchPricesAndOrderAmounts(
        apiBaseUrl,
        [{ productId, quantity }],
        product.customerContext.customerContextId,
        shipToLocationId,
      )

      return pricesAndOrderAmounts.find(p => p.productId === productId)
    }
  }

  const priceAndOrderAmount = await fetchPriceAndOrderAmountFromApi()
  const { orderAmount, prices: pricePoints, subtotalValue } =
    priceAndOrderAmount ?? ({} as ProductCalculation)
  const price = PriceType.fromMonetaryAmount(subtotalValue)

  return { orderAmount, price, pricePoints }
}

const getMinimumOrderQuantity = (
  pricePoints: PricePointAttributes[],
  packaging: Packaging,
) => {
  const minimumOrderAmount = getMinimumOrderAmount(pricePoints)
  if (minimumOrderAmount && packaging.size.uom === minimumOrderAmount.uom) {
    const minimumOrderQuantity = Math.ceil(
      minimumOrderAmount.quantity / packaging.size.quantity,
    )
    return minimumOrderQuantity === 0 ? 1 : minimumOrderQuantity
  } else {
    return 1
  }
}

interface Props {
  isOpen: boolean
  mode: 'add' | 'edit'
  onClose: () => void
  onUpdateProductQuantity: (product: BaseCartProduct) => void
  product: BaseCartProduct | undefined
  productIsAvailable?: boolean
  shipToLocationId: string | undefined
}

export const UpdateOrderLineModal = ({
  isOpen,
  mode,
  onClose,
  onUpdateProductQuantity,
  product,
  productIsAvailable = true,
  shipToLocationId,
}: Props) => {
  const t = useTranslations()
  const u = useUom()
  const { showErrorToast } = useToaster()
  const apiBaseUrl = useSelector(ConfigSelector.apiBaseUrl)

  const [quantity, setQuantity] = useState<string | undefined>('')
  const [orderAmount, setOrderAmount] = useState<string | undefined>('')
  const quantityNumber = Number.parseInt(quantity ?? '1', 10)
  const orderAmountNumber = Number.parseFloat(orderAmount ?? '1')
  const originalQuantityRef = useRef<string | undefined>('')
  const [orderAmountUom, setOrderAmountUom] = useState<string | undefined>(
    undefined,
  )
  const [price, setPrice] = useState<PriceType | undefined>(undefined)
  const [pricePoints, setPricePoints] = useState<
    PricePointAttributes[] | undefined
  >(undefined)

  const [isLoading, setIsLoading] = useState(false)
  const [dataError, setError] = useState(false)
  const [isAmountRecalcRequired, setIsAmountRecalcRequired] = useState<boolean>(
    false,
  )
  const [showRoundingMessage, setShowRoundingMessage] = useState<boolean>(false)

  const isUpdateMode = product?.quantity !== undefined
  const isQuantityUnchanged = quantity === originalQuantityRef.current

  const updatePriceAndOrderAmount = useCallback(
    async (quantity: number) => {
      setIsLoading(true)

      const response = await fetchPriceAndOrderAmount(
        apiBaseUrl,
        shipToLocationId,
        product,
        quantity,
      )

      const {
        orderAmount: orderAmountToSet,
        price: priceToSet,
        pricePoints: pricesToSet,
      } = response

      if (priceToSet === undefined || orderAmountToSet === undefined) {
        showErrorToast(t('toast.error.price'))
        setError(true)
        setIsLoading(false)
        return
      } else {
        setError(false)
      }

      setPrice(priceToSet)
      setPricePoints(pricesToSet)
      setOrderAmount(String(quantity === 0 ? 0 : orderAmountToSet?.quantity))
      setIsAmountRecalcRequired(false)

      if (orderAmountToSet) {
        setOrderAmountUom(orderAmountToSet.uom)
      }

      setIsLoading(false)
    },
    [apiBaseUrl, product, shipToLocationId, showErrorToast, t],
  )

  const minimumOrderQuantity = product
    ? getMinimumOrderQuantity(product.pricePoints, product.data.packaging)
    : undefined

  const updateQuantity = useCallback(
    (quantityToQuery: number) => updatePriceAndOrderAmount(quantityToQuery),
    [updatePriceAndOrderAmount],
  )

  useEffect(() => {
    if (isOpen) {
      const quantity = product?.quantity || minimumOrderQuantity || 1
      updateQuantity(quantity)
      originalQuantityRef.current = String(quantity)
      setQuantity(String(quantity))
    }

    return () => {
      setQuantity('')
      setOrderAmount('')
      setOrderAmountUom(undefined)
      setShowRoundingMessage(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const onQuantity = (quantity: string | undefined) => {
    const numericQuantity = Number(quantity)
    setQuantity(quantity)
    if (numericQuantity) {
      debounce(500, updateQuantity)(numericQuantity)
    }
  }

  const onQuantityChanged = (quantity: string | undefined) => {
    onQuantity(quantity)
    setShowRoundingMessage(false)
  }

  const onAmountChanged = (amount: string | undefined) => {
    setOrderAmount(amount)
    setIsAmountRecalcRequired(true)
  }

  const onFooterActionClick = () => {
    if (product && quantityNumber) {
      onUpdateProductQuantity({
        ...product,
        orderAmount: {
          quantity: orderAmountNumber,
          uom: orderAmountUom!,
        },
        price,
        pricePoints: pricePoints || [],
        quantity: quantityNumber,
      })
    }
    onClose()
  }

  const onFooterCalculateClick = () => {
    if (orderAmountNumber) {
      const size = product?.data.packaging.size.quantity ?? 1
      const newQuantity = Math.ceil(
        Math.floor((orderAmountNumber / size) * 1e7) / 1e7,
      ) // fixes js rounding error 0.1 + 0.2 = 0.300000000004
      setShowRoundingMessage(
        Boolean(newQuantity) &&
          Math.abs(orderAmountNumber - newQuantity * size) > 1e-7,
      )
      onQuantity(String(newQuantity))
    }
  }

  const quantityError =
    quantityNumber <= 0
      ? t('label.quantity.zero')
      : minimumOrderQuantity !== undefined &&
        minimumOrderQuantity > 1 &&
        quantityNumber < minimumOrderQuantity
      ? t('label.quantity.minimumorderquantity').replace(
          '@@MINIMUMORDERQUANTITY@@',
          `${minimumOrderQuantity}`,
        )
      : quantityNumber > 1e10 - 1
      ? t('label.quantity.toolarge')
      : undefined

  const minimumOrderAmount = product?.data.packaging.size
    ? (minimumOrderQuantity ?? 0) * product.data.packaging.size.quantity
    : 0

  const amountError =
    orderAmountNumber <= 0
      ? t('label.quantity.zero')
      : minimumOrderQuantity !== undefined &&
        minimumOrderQuantity > 1 &&
        orderAmountNumber < minimumOrderAmount
      ? t('label.quantity.minimumorderamount').replace(
          '@@MINIMUMORDERAMOUNT@@',
          `${minimumOrderAmount}`,
        )
      : orderAmountNumber > 1e10 - 1
      ? t('label.quantity.toolarge')
      : undefined

  const isAddToCartDisabled =
    isLoading || dataError || Boolean(quantityError) || Boolean(amountError)
  const isActionDisabled =
    mode === 'add'
      ? isAddToCartDisabled
      : isAddToCartDisabled || (isQuantityUnchanged && !isAmountRecalcRequired)

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      motionPreset="scale"
      onClose={onClose}
      size="3xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading
            color="deepPurple"
            size="xl"
            textAlign="left"
            textTransform="capitalize"
          >
            {product?.data?.name}
          </Heading>
        </ModalHeader>
        <ModalCloseButton aria-label="close" onClose={onClose} />
        <ModalBody>
          <Collapse animateOpacity in={!productIsAvailable}>
            <Box mb={4}>
              <InfoMessage
                fullWidth
                icon={<IconLocked boxSize="20px" />}
                type="error"
              >
                {t('location.shipto.notavailable')}
              </InfoMessage>
            </Box>
          </Collapse>

          <Stack direction={['column', 'column', 'row']} spacing={5}>
            <VStack alignItems="unset" justify="stretch" spacing={5} w="100%">
              <Box pb={5}>
                <Text fontWeight="bold">{t('label.orderdetails')}</Text>
              </Box>
              <Flex direction="column">
                <InputLabel id="input.quantity">
                  {t('label.quantity')}
                </InputLabel>
                <Input
                  aria-labelledby="input.quantity"
                  error={Boolean(quantityError)}
                  helper={quantityError}
                  onValueChange={onQuantityChanged}
                  pattern="^\d+$"
                  type="text"
                  unit={
                    product?.data.packaging.size
                      ? `${product.data.packaging.size.quantity} ${u(
                          product.data.packaging.size.uom,
                        )} ${product.data.packaging.type}`
                      : undefined
                  }
                  value={quantity}
                />
              </Flex>

              <Flex direction="column">
                <InputLabel id="input.orderamount">
                  {t('label.orderamount')}
                </InputLabel>
                <Input
                  aria-labelledby="input.orderamount"
                  error={Boolean(amountError)}
                  helper={amountError}
                  isDisabled={isLoading}
                  onBlur={onFooterCalculateClick}
                  onValueChange={onAmountChanged}
                  pattern="^\d+(\.\d{0,3})?$"
                  type="text"
                  unit={u(orderAmountUom)}
                  value={orderAmount}
                />
              </Flex>

              <Box h={{ md: '60px' }} maxW={{ md: '350px' }}>
                {showRoundingMessage && (
                  <InfoMessage
                    fullWidth
                    narrow
                    icon={<IconCircleAlert boxSize="16px" />}
                    type="info"
                  >
                    {t('cart.recalculate.rounding')}
                  </InfoMessage>
                )}
              </Box>
            </VStack>
            <VStack alignItems="unset" justify="stretch" spacing={5} w="100%">
              <Flex direction="column">
                <Box mb="44px" pb={5}>
                  <Text fontWeight="bold">{t('label.price')}</Text>
                </Box>
                <TierPrices
                  orderAmount={orderAmountNumber}
                  pricePoints={pricePoints ?? []}
                >
                  <NoPriceAvailable />
                </TierPrices>
              </Flex>
            </VStack>
          </Stack>
        </ModalBody>
        <ModalFooter justifyContent="center">
          <GenericModalFooter
            actionButtonId="modal-add-to-cart-btn"
            actionIcon={isUpdateMode ? 'IconCheckmark' : 'IconCart'}
            actionTextLabel={
              isUpdateMode ? 'cart.updatecart' : 'cart.addtocart'
            }
            isActionDisabled={isActionDisabled}
            isLoadingAction={isLoading}
            isReactionRequired={isAmountRecalcRequired}
            onAction={onFooterActionClick}
            onCancel={onClose}
            onReaction={onFooterCalculateClick}
            reactionButtonId="modal-calculate-btn"
            reactionIcon="IconChange"
            reactionTextLabel="cart.recalculate"
          >
            <PriceFooter show={Boolean(price)}>
              <CaptionText as="span" color="blue.50">
                {t('label.ordervalue')}:&nbsp;
              </CaptionText>
              {price ? <Price>{price}</Price> : <LoadingBars />}
            </PriceFooter>
          </GenericModalFooter>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
